import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { ButtonSize, PureButton, ShareIcon, WishlistButtonType } from '@jsmdg/yoshi';
import { dispatchEvent } from '../../helper/EventUtils';
import { useProduct } from '../../hooks/useProduct';
import { eventTypes } from '../../types';
import { type ProductDetailContext } from '../../types/types';
import { AddToWishlistButton } from '../AddToWishlistButton/AddToWishlistButton';
import { trackRatingClicked } from '../BuyboxContainer/buyboxContainer.tracking';
import { Nudge } from '../Nudge/Nudge';
import { RatingAndParticipantsContainer } from '../RatingAndParticipantsContainer/RatingAndParticipantsContainer';
import { UspText } from './UspText';
import styles from '../Buybox/Buybox.module.scss';

const messages = defineMessages({
    a11YCloseErrorMessage: { defaultMessage: 'Schließen' },
    shareCTAMessage: { defaultMessage: 'Erlebnis teilen' },
});

const handleRatingClick = (): void => {
    dispatchEvent(eventTypes.RATINGS_CLICK);
    trackRatingClicked();
};

const BuyboxHeader = (): JSX.Element => {
    const { title, uspText } = useProduct();
    const intl = useIntl();
    const { tenantConfig } = useFragmentContext<ProductDetailContext>();

    return (
        <div className="d-flex flex-column w-100">
            <div
                className={classNames(
                    styles.voucherTitle,
                    'd-flex align-items-center justify-content-between',
                )}
            >
                <FormattedMessage
                    defaultMessage="{company} Gutschein"
                    values={{
                        company: tenantConfig.shareTitle,
                    }}
                />
                <div className="d-flex align-items-center justify-content-center gap-1x">
                    <PureButton
                        onClick={() => dispatchEvent(eventTypes.OPEN_SHARE_MODAL)}
                        aria-label={intl.formatMessage(messages.shareCTAMessage)}
                        className={classNames(
                            styles.shareButton,
                            'd-sm-none d-flex align-items-center justify-content-center',
                        )}
                    >
                        <ShareIcon />
                    </PureButton>
                    <AddToWishlistButton
                        isTransparent={false}
                        a11yCloseButtonText={intl.formatMessage(messages.a11YCloseErrorMessage)}
                        size={ButtonSize.Small}
                        buttonClasses={classNames(styles.wishlistButtonWrapper, 'flex-shrink-0')}
                        dataTestId="wishlist-toggle-button-pdp-buybox"
                        wishlistButtonType={WishlistButtonType.WithOutline}
                    />
                </div>
            </div>
            <div className="d-flex w-100">
                <h1
                    id="product-headline"
                    className={classNames('theme-text-headline w-100', styles.headline)}
                >
                    {title}
                </h1>
            </div>
            {uspText && <UspText uspText={uspText} />}
            <RatingAndParticipantsContainer handleRatingClick={handleRatingClick} />
            <Nudge />
        </div>
    );
};

export { BuyboxHeader };
